
import { Component, Vue, Prop, Watch } from "vue-property-decorator"
import { Getter, Mutation, namespace, State } from "vuex-class"

import api from "@/services/api"
import { CaseDetails, Schedule, TimeZone } from "@/types"
import ScheduleEditService from "@/services/scheduleEditService"
import { CurrentUser } from "@/types/user"
import DataForm from "@/components/Config/DataForm.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { ScheduleMessage } from "@/types/schedule"
const caseStore = namespace("cases")

@Component({
	components: {
		"data-form": DataForm
	}
})
export default class CaseSchedule extends Vue {
	@Prop()
	public currentCase!: CaseDetails;

	@Prop() public schedule!: Schedule

	@Prop() public showScheduleModal!: CurrentUser

	@State public me!: CurrentUser;

	@State tenantSettings!: { [key: string]: any } | null

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter("getRecordName") getRecordName!: string;
	@Getter("getTenantTimezones") tenantTimezones!: TimeZone[]

	@Mutation private setScheduleToastMessage!: (message: ScheduleMessage | null) => void
	@caseStore.Mutation private setCaseRefresh!: (refresh: boolean | null) => void

	public scheduleEditService = new ScheduleEditService()

	public isValid: boolean = true;

	public get canEditSchedules (): boolean {
		return (this.me && (this.me.extendedPermissions && this.me.extendedPermissions["scheduling"])) || false
	}

	public async mounted () {
		await this.initializeScheduleEditService()
	}

	private async initializeScheduleEditService () {
		await this.scheduleEditService.load(this.me, false)
		// data
		this.scheduleEditService.recordName = this.getRecordName
		this.scheduleEditService.tenantTimeZones = this.tenantTimezones

		// tenant features
		this.scheduleEditService.getIsFeatureEnabled = this.getIsFeatureEnabled
		this.scheduleEditService.initializeFeatures()

		this.scheduleEditService.currentSchedule = this.schedule!
		if (this.schedule) {
			this.scheduleEditService.templateChanged(this.schedule.templateId, true)
		}
	}

	public async saveSchedule (schedule: Schedule): Promise<void> {
		if (this.getIsFeatureEnabled(TenantFeatures.ScheduleForecastPopup)) {
			const toastTitle = (/schedule/i).test(schedule.title) ? schedule.title : `${schedule.title} Schedule`
			this.setScheduleToastMessage(
				{
					scheduleId: schedule.title,
					message: `Forecasting ${this.getRecordName}s for ${toastTitle}`,
					variant: "info",
					autoClose: false
				})
		}

		await this.scheduleEditService.saveSchedule(schedule)

		// Refresh data
		this.setCaseRefresh(true)
		this.closeScheduleModal()
	}

	private get scheduleColumns () {
		if (!this.scheduleEditService) return []
		return this.scheduleEditService.columns.filter((c: any) => {
			if (c.key === "endTime") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideTimeSpan"]
			}
			if (c.key === "bulkCreateForTeams") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideBulkCreate"]
			}
			if (c.key === "excludeWeekends") {
				return this.getIsFeatureEnabled(TenantFeatures.SchedulingExcludeWeekends)
			}
			if (c.key === "nextRunTime") {
				return this.getIsFeatureEnabled(TenantFeatures.ScheduleDisplayRunTimes)
			}
			return true
		})
	}

	public closeScheduleModal () {
		this.$emit("closeScheduleModal")
	}

	@Watch("scheduleEditService.currentSchedule.minRunDate")
	private minRunDateChanged (selectedDate: Date) {
		this.scheduleEditService.minRunDateChanged(selectedDate)
	}

	@Watch("scheduleEditService.currentSchedule.maxRunDate")
	private maxRunDateChanged (selectedDate: Date) {
		this.scheduleEditService.maxRunDateChanged(selectedDate)
	}
}
